import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AddButton } from "../../components/common/AddButton";
import PageTitle from "../../components/common/PageTitle";
import { BusinessIcon } from "../../components/common/pageIcons";
import PageLayout from "../../components/layout/PageLayout";
import {
  Tenant,
  useDeleteTenantMutation,
  useGetTenantsPaginatedQuery,
} from "../../api/tenantsApi";
import TenantsList from "./components/TenantsList";
import DeleteTenantDialog from "./components/DeleteTenantDialog";
import { useInfiniteDataTableItems } from "../../hooks/useInfiniteDataTableItems";
import { DEFAULT_DATA_TABLE_PAGE_SIZE } from "../../constants/dataTable";
import { useToast } from "../../contexts/ToastContext";
import { extractErrorMessage } from "../../utils/errorUtils";

const TenantsPage: React.FC = () => {
  const navigate = useNavigate();
  const { showError, showSuccess } = useToast();
  const [page, setPage] = useState(0);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);

  const { data, isLoading, isFetching } = useGetTenantsPaginatedQuery({
    pageNumber: page + 1,
    pageSize: DEFAULT_DATA_TABLE_PAGE_SIZE,
  });
  const { accumulatedItems: tenants, resetAccumulatedItems } =
    useInfiniteDataTableItems<Tenant>(data?.items, page);
  const [deleteTenant, { isLoading: isDeleting }] = useDeleteTenantMutation();

  const resetTenantTable = () => {
    resetAccumulatedItems();
    setPage(0);
  };

  const handleEditClick = (tenant: Tenant) => {
    navigate(`/tenants/${tenant.id}/edit`);
  };

  const handleDeleteClick = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    setIsDeleteModalOpen(true);
  };

  const handleCreateTenant = () => {
    navigate("/tenants/create");
  };

  const handleDeleteConfirm = async () => {
    if (!selectedTenant) return;
    try {
      await deleteTenant(selectedTenant.id).unwrap();
      showSuccess(`Tenant "${selectedTenant.name}" deleted`);
      resetTenantTable();
      setIsDeleteModalOpen(false);
      setSelectedTenant(null);
    } catch (error) {
      showError(extractErrorMessage(error));
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
    setSelectedTenant(null);
  };

  const handleLoadMore = () => {
    if (data?.hasNextPage && !isFetching) {
      setPage((currentPage) => currentPage + 1);
    }
  };

  return (
    <PageLayout>
      <PageTitle
        title="Tenants"
        icon={<BusinessIcon />}
        actions={<AddButton onClick={handleCreateTenant}>Add Tenant</AddButton>}
      />

      <TenantsList
        tenants={tenants}
        isLoading={isLoading && tenants.length === 0}
        onEditClick={handleEditClick}
        onDeleteClick={handleDeleteClick}
        totalCount={data?.totalCount}
        hasNextPage={data?.hasNextPage}
        isLoadingMore={isFetching && tenants.length > 0}
        onLoadMore={handleLoadMore}
      />

      <DeleteTenantDialog
        open={isDeleteModalOpen}
        tenant={selectedTenant}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        isLoading={isDeleting}
      />
    </PageLayout>
  );
};

export default TenantsPage;
